import { api, readCache } from './api'
import { getMe, isWorker } from './me'
import { kindLabel } from './trade'
import { visitJobId, type CustomerJob, type JobKind } from './jobs'
import type { Clean, Customer } from './types'

// The owner screens' copy of every extra job on the round (GET /api/customer-jobs, several jobs step 2), so the
// diary, Month, Owed, the bank screen and Messages can name a visit's job and work per job. Read alongside each
// screen's own read, and kept on the phone like any other read. Nothing here on a round with several jobs off: no
// read is made and every visit is labelled exactly as before.

let jobs: CustomerJob[] = []
let byId = new Map<number, CustomerJob>()
let multi = new Set<number>()
// Job 1's own names (customers.job_name), from whichever screen last read the customers.
const jobOneNames = new Map<number, string>()

export function severalJobsOn(): boolean {
  return getMe()?.severalJobs === true
}

function keep(list: CustomerJob[]): CustomerJob[] {
  jobs = list
  byId = new Map(list.map((j) => [j.id, j]))
  multi = new Set(list.filter((j) => !j.deleted_at).map((j) => j.customer_id))
  return list
}

// Every running and stopped extra job, each with its own dates. Empty while the round's switch is off, for a
// worker, or when nothing can be read (the saved copy then, if there is one).
export async function loadRoundJobs(onFailed?: () => void): Promise<CustomerJob[]> {
  if (!severalJobsOn() || isWorker()) return keep([])
  try {
    const data = await api<{ jobs: CustomerJob[] }>('/api/customer-jobs', 'customer-jobs')
    return keep(data.jobs ?? [])
  } catch {
    onFailed?.()
    return keep(readCache<{ jobs: CustomerJob[] }>('customer-jobs')?.data.jobs ?? jobs)
  }
}

export function roundJobs(): CustomerJob[] {
  return jobs
}

export function jobsOfCustomer(customerId: number): CustomerJob[] {
  return jobs.filter((j) => j.customer_id === customerId && !j.deleted_at)
}

// Remember job 1's own names from a customers read (only customers with one are kept).
export function noteJobOneNames(customers: Pick<Customer, 'id' | 'job_name'>[]): void {
  for (const c of customers) {
    if (c.job_name && c.job_name.trim()) jobOneNames.set(c.id, c.job_name.trim())
    else jobOneNames.delete(c.id)
  }
}

// Does this customer have more than one job? Then their visits are named by job everywhere.
export function hasExtraJobs(customerId: number): boolean {
  return multi.has(customerId)
}

// The word on a visit's badge. A customer with one job: the trade's word for the visit's kind, exactly as before.
// With more: the job's own name (job 1's name if it has one), else the trade's word for its kind.
export function visitJobLabel(v: Pick<Clean, 'customer_id' | 'kind'> & { job_id?: number | null; job_name?: string | null }): string {
  const job = visitJobId(v)
  if (v.job_name && v.job_name.trim() && (job !== null || hasExtraJobs(v.customer_id))) return v.job_name.trim()
  if (!severalJobsOn() || !hasExtraJobs(v.customer_id)) return kindLabel(v.kind)
  if (job === null) return jobOneNames.get(v.customer_id) ?? kindLabel(v.kind)
  return byId.get(job)?.name ?? kindLabel(v.kind)
}

// A job's word in a sentence to a customer, lower case: "gutters", "windows", "conservatory roof".
export function jobWordLower(name: string | null | undefined, kind: JobKind | null | undefined): string {
  const w = (name && name.trim()) || kindLabel(kind ?? 'window')
  return w.toLowerCase()
}
